/* ==========================================================================
   nl-carousel.js — one shared carousel behavior for every gn- section.
   Configured entirely via data-* attributes; no styling classes are used as
   hooks. Supports two modes so we never fork a second half-featured carousel:
     data-nl-carousel="fade"   → cross-fade slides (PDP gallery)
     data-nl-carousel="slide"  → horizontal track, N per view (testimonials)

   Root attributes:
     data-nl-carousel   "fade" | "slide"            (required)
     data-nl-autoplay   seconds (0 / absent = off)
     data-nl-loop       "true" to wrap around
   Child hooks (all optional except slides):
     [data-nl-slide]            each slide (index = DOM order)
     [data-nl-track]            (slide mode) the flex track that translates
     [data-nl-thumb]            thumbnail button → goes to matching slide
     [data-nl-dots]             empty container; dots are generated into it
     [data-nl-prev] / [data-nl-next]  arrow buttons
     [data-nl-status]           aria-live region updated to "n of total"
     [data-nl-per-view]         (slide mode, on root) desktop slides per view
     [data-nl-media-id]         (on a slide) media id for nl:show-media and
                                data-nl-initial-media-id; the legacy
                                data-media-id is still read when absent

   Editor lifecycle: guards double-init, re-inits on shopify:section:load,
   tears down timers/listeners on shopify:section:unload. Respects
   prefers-reduced-motion (no autoplay).
   ========================================================================== */
(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var instances = [];

  function slideMediaId(slide) {
    return slide.getAttribute('data-nl-media-id') || slide.getAttribute('data-media-id');
  }

  function perView(root) {
    if (root.getAttribute('data-nl-carousel') !== 'slide') return 1;
    var n = parseInt(root.getAttribute('data-nl-per-view'), 10);
    if (!n || n < 1) n = 3;
    if (window.matchMedia('(max-width: 767px)').matches) return 1;
    if (window.matchMedia('(max-width: 1023px)').matches) return Math.min(2, n);
    return n;
  }

  function Carousel(root) {
    this.root = root;
    this.mode = root.getAttribute('data-nl-carousel') || 'fade';
    this.loop = root.getAttribute('data-nl-loop') === 'true';
    // Responsive-static: above this width the carousel goes inert and the
    // markup is laid out as a plain CSS grid (0 = always a carousel).
    this.staticAbove = parseInt(root.getAttribute('data-nl-static-above'), 10) || 0;
    this.autoplayMs = (parseFloat(root.getAttribute('data-nl-autoplay')) || 0) * 1000;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-nl-slide]'));
    this.track = root.querySelector('[data-nl-track]');
    this.thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-nl-thumb]'));
    this.dotsWrap = root.querySelector('[data-nl-dots]');
    this.prevBtn = root.querySelector('[data-nl-prev]');
    this.nextBtn = root.querySelector('[data-nl-next]');
    this.statusEl = root.querySelector('[data-nl-status]');
    this.index = 0;
    this.timer = null;
    this._bound = [];
    if (this.slides.length) this.init();
  }

  Carousel.prototype.on = function (el, ev, fn, opts) {
    if (!el) return;
    el.addEventListener(ev, fn, opts);
    this._bound.push([el, ev, fn, opts]);
  };

  Carousel.prototype.isStatic = function () {
    return this.staticAbove && window.matchMedia('(min-width: ' + this.staticAbove + 'px)').matches;
  };

  Carousel.prototype.pages = function () {
    if (this.mode !== 'slide') return this.slides.length;
    // Loop mode: one step per card, clones make the wrap seamless
    if (this.loop) return this.slides.length;
    return Math.max(1, this.slides.length - perView(this.root) + 1);
  };

  Carousel.prototype.init = function () {
    var self = this;

    // Slide+loop: append clones of the leading slides so the forward wrap is
    // seamless (continuous loop, one card at a time)
    if (this.mode === 'slide' && this.loop && this.track && this.slides.length > 1) {
      var cloneCount = parseInt(this.root.getAttribute('data-nl-per-view'), 10) || 3;
      for (var k = 0; k < cloneCount; k++) {
        var clone = this.slides[k % this.slides.length].cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-nl-clone', '');
        this.track.appendChild(clone);
      }
    }

    // Build dots (none when there's only one page — a lone dot is noise)
    if (this.dotsWrap) {
      this.dotsWrap.innerHTML = '';
      var pageCount = this.pages();
      if (pageCount < 2) pageCount = 0;
      for (var d = 0; d < pageCount; d++) {
        (function (i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'nl-carousel__dot';
          dot.setAttribute('data-nl-dot', String(i));
          dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          self.on(dot, 'click', function () { self.go(i, true); });
          self.dotsWrap.appendChild(dot);
        })(d);
      }
      this.dots = Array.prototype.slice.call(this.dotsWrap.querySelectorAll('[data-nl-dot]'));
    }

    this.thumbs.forEach(function (t, i) {
      self.on(t, 'click', function () { self.go(i, true); });
    });
    this.on(this.prevBtn, 'click', function () { self.go(self.index - 1, true); });
    this.on(this.nextBtn, 'click', function () { self.go(self.index + 1, true); });
    this.on(this.root, 'nl:show-media', function (event) {
      var mediaId = event.detail && String(event.detail.mediaId || '');
      if (!mediaId) return;
      for (var m = 0; m < self.slides.length; m++) {
        if (slideMediaId(self.slides[m]) === mediaId) {
          self.go(m, false);
          break;
        }
      }
    });

    // Keyboard on the root
    this.on(this.root, 'keydown', function (e) {
      if (e.key === 'ArrowLeft') { self.go(self.index - 1, true); }
      else if (e.key === 'ArrowRight') { self.go(self.index + 1, true); }
    });

    // Optional pointer/touch swipe (opt-in via data-nl-swipe="true").
    // Listens on the track's parent (the viewport); CSS touch-action: pan-y
    // lets vertical scroll pass through while we capture horizontal drags.
    if (this.root.getAttribute('data-nl-swipe') === 'true' && this.track) {
      var swiper = this.track.parentNode;
      var startX = 0, dragging = false;
      this.on(swiper, 'pointerdown', function (e) {
        if (self.isStatic()) return;
        dragging = true; startX = e.clientX;
      });
      this.on(swiper, 'pointerup', function (e) {
        if (!dragging) return;
        dragging = false;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 40) self.go(self.index + (dx < 0 ? 1 : -1), true);
      });
      this.on(swiper, 'pointercancel', function () { dragging = false; });
    }

    // Pause autoplay on interaction
    if (this.autoplayMs && !REDUCE) {
      this.on(this.root, 'mouseenter', function () { self.stop(); });
      this.on(this.root, 'mouseleave', function () { self.start(); });
      this.on(this.root, 'focusin', function () { self.stop(); });
      this.on(this.root, 'focusout', function () { self.start(); });
      this._resize = function () { self.go(self.index, false); };
      this.on(window, 'resize', this._resize);
      this.start();
    } else {
      this._resize = function () { self.go(self.index, false); };
      this.on(window, 'resize', this._resize);
    }

    var initialMediaId = this.root.getAttribute('data-nl-initial-media-id');
    var initialIndex = 0;
    if (initialMediaId) {
      for (var m = 0; m < this.slides.length; m++) {
        if (slideMediaId(this.slides[m]) === initialMediaId) { initialIndex = m; break; }
      }
    }
    this.go(initialIndex, false);
  };

  Carousel.prototype.go = function (i, user) {
    // Responsive-static mode: above the breakpoint the section is a plain CSS
    // grid — clear any transform, hold at slide 0, let CSS hide the controls.
    if (this.isStatic()) {
      if (this.track) this.track.style.transform = '';
      this.index = 0;
      return;
    }
    var count = this.pages();
    var self = this;

    if (this.mode === 'slide' && this.loop && this.track && count > 1) {
      // Continuous loop, one card per step: forward wrap rides onto the
      // clones, then snaps back to 0 without transition.
      if (i < 0) i = count - 1;
      if (i > count) i = count;
      var physical = i;
      this.index = i === count ? 0 : i;
      this.track.style.transform = 'translateX(' + (-(physical * (100 / perView(this.root)))) + '%)';
      if (physical === count) {
        var onEnd = function () {
          self.track.removeEventListener('transitionend', onEnd);
          var prevTransition = self.track.style.transition;
          self.track.style.transition = 'none';
          self.track.style.transform = 'translateX(0%)';
          void self.track.offsetWidth;
          self.track.style.transition = prevTransition;
        };
        this.track.addEventListener('transitionend', onEnd);
      }
    } else {
      if (i < 0) i = this.loop ? count - 1 : 0;
      if (i > count - 1) i = this.loop ? 0 : count - 1;
      this.index = i;

      if (this.mode === 'fade') {
        this.slides.forEach(function (s, si) {
          var active = si === i;
          s.classList.toggle('nl-carousel__slide--active', active);
          s.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      } else if (this.track) {
        var pct = -(i * (100 / perView(this.root)));
        this.track.style.transform = 'translateX(' + pct + '%)';
      }
    }
    i = this.index; // indicators reflect the logical card

    if (this.dots) {
      this.dots.forEach(function (dot, di) {
        var on = di === i;
        dot.classList.toggle('nl-carousel__dot--active', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
    this.thumbs.forEach(function (t, ti) {
      t.classList.toggle('nl-carousel__thumb--active', ti === i);
      t.setAttribute('aria-current', ti === i ? 'true' : 'false');
    });

    if (this.prevBtn && !this.loop) this.prevBtn.disabled = i === 0;
    if (this.nextBtn && !this.loop) this.nextBtn.disabled = i === count - 1;
    if (this.statusEl) this.statusEl.textContent = (i + 1) + ' of ' + count;

    // Keep active thumb in view
    if (user && this.thumbs[i] && this.thumbs[i].scrollIntoView) {
      try { this.thumbs[i].scrollIntoView({ inline: 'nearest', block: 'nearest' }); } catch (e) {}
    }
    if (user) this.restart();
  };

  Carousel.prototype.start = function () {
    if (!this.autoplayMs || REDUCE || this.pages() < 2) return;
    var self = this;
    this.stop();
    this.timer = window.setInterval(function () { self.go(self.index + 1, false); }, this.autoplayMs);
  };
  Carousel.prototype.stop = function () {
    if (this.timer) { window.clearInterval(this.timer); this.timer = null; }
  };
  Carousel.prototype.restart = function () { if (this.timer) { this.stop(); this.start(); } };

  Carousel.prototype.destroy = function () {
    this.stop();
    this._bound.forEach(function (b) { b[0].removeEventListener(b[1], b[2], b[3]); });
    this._bound = [];
    // Remove loop clones so a re-init doesn't count them as slides
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-nl-clone]'), function (c) {
      if (c.parentNode) c.parentNode.removeChild(c);
    });
    this.root.__mtCarousel = null;
  };

  function initWithin(scope) {
    var roots = (scope || document).querySelectorAll('[data-nl-carousel]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__mtCarousel) return; // guard double init
      var inst = new Carousel(root);
      root.__mtCarousel = inst;
      instances.push(inst);
    });
  }

  function destroyWithin(scope) {
    var roots = (scope || document).querySelectorAll('[data-nl-carousel]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__mtCarousel) root.__mtCarousel.destroy();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initWithin(document); });
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', function (e) { initWithin(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroyWithin(e.target); });
})();
